import Loader from '../layout/Loader';
import AuthLayout from '../layout/AuthLayout';
import { cookieDecryption, handleLogOut } from '../utils';
import { memo, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useLocation, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { tokenExpirationHandler } from '../utils/tokenExpirationHandler';
import { navigateAfterLogin } from '../utils/messageRoutes';

const PublicRouter = ({ title, layout }) => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const token = params.get('token');
  const shortLivedToken = params.get('shortLivedToken');
  const expiryToken = params.get('expiryToken');
  const id = params.get('id');

  // Legacy deep links send the bare word 'messages'; newer ones send a path.
  const redirectUrl = params.get('redirect');
  const wantsMessages = redirectUrl === 'messages' || redirectUrl === '/messages';
  const auth = cookieDecryption('userDetails');

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!!token) {
      handleLogOut();
      navigate(`/?token=${token}`);
    }
  }, [token]);
  // Fire mobile deep-link for short-lived SSO token even if user is already logged in on web
  useEffect(() => {
    if (!shortLivedToken) return;
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = isAndroid || isIOS;

    if (isMobile) {
      const schemeUrl = `imentordev://login?shortLivedToken=${encodeURIComponent(shortLivedToken)}`;
      const androidIntentUrl = `intent://login?shortLivedToken=${encodeURIComponent(shortLivedToken)}#Intent;scheme=imentordev;package=org.imentor.applicationstage;end`;
      const started = Date.now();
      try {
        window.location.href = schemeUrl;
      } catch {}
      setTimeout(() => {
        if (Date.now() - started < 1800 && isAndroid) {
          try {
            window.location.href = androidIntentUrl;
          } catch {}
        }
      }, 400);
    }
  }, [shortLivedToken]);
  useEffect(() => {
    if (expiryToken) {
      tokenExpirationHandler.handleExpiration(expiryToken, navigate);
    }
    if (!!id && window.location?.pathname.includes('register')) {
      handleLogOut();
      navigate(`/register/?id=${id}`);
    }
  }, [id]);
  useEffect(() => {
    if (wantsMessages && auth) {
      // Full page load so the chat socket bootstraps. A reload here would
      // re-load /?redirect=messages and re-run this effect on the way back.
      navigateAfterLogin(navigate, '/messages');
      return null;
    }
  }, []);

  let redirectPath = '/home';
  const getLayout = layout => {
    switch (layout) {
      case 'AUTH_LAYOUT': {
        return (
          <AuthLayout>
            <Outlet context={['sohel']} />
          </AuthLayout>
        );
      }
      default: {
        return <Outlet context={['sohel']} />;
      }
    }
  };

  return !auth ? (
    <Suspense fallback={<Loader />}>
      <HelmetProvider context={{}}>
        <title>{title}</title>
        {getLayout(layout)}
      </HelmetProvider>
    </Suspense>
  ) : (
    <Navigate to={redirectPath} state={{ from: location }} replace />
  );
};

export default memo(PublicRouter);
